import clsx from 'clsx'
import Sidebar from '@Components/Sidebars/Sidebar'
import FilterSection from '@Components/Search/SearchWithCriteria/FilterSection'
import React, { useMemo, useState } from 'react'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { FilterValues, SearchPanelSortOption } from '@Components/Search/SearchPanel/types'
import { SearchWithCriteriaFormType } from '@Components/Search/SearchWithCriteria/form-type'
import { UseFormReturn } from 'react-hook-form'
import { stripEmptyFilters } from '@Components/Search/SearchPanel/utils'
import FilterControls from '@Components/Search/SearchPanel/FilterControls'

interface SearchFilterProps<K extends object> {
    filterFieldCollections?: Array<FilterFieldGroup<K>>
    onFilterValueChange?: (filters: FilterValues<K> | undefined) => void
    onApplyFilters?: (filters: FilterValues<K> | undefined) => void
    methods: UseFormReturn<SearchWithCriteriaFormType<K>>
    onSubmit: (data: SearchWithCriteriaFormType<K>) => void
    defaultFilterValues?: FilterValues<K>
    sortOptions?: SearchPanelSortOption[]
    sortFilterKey?: keyof K & string
    sortValue?: string
    onSortChange?: (value: string) => void
}

/** Number of individually selected filter values (each checkbox-group entry counts on its own). */
const countActiveFilters = <K extends object>(filters: FilterValues<K> | undefined): number => {
    if (!filters) {
        return 0
    }

    return Object.values(filters).reduce<number>((total, value) => {
        if (Array.isArray(value)) {
            return total + value.filter((v) => v !== '' && v !== null && v !== undefined).length
        }
        if (value === null || value === undefined || value === '' || value === false) {
            return total
        }
        return total + 1
    }, 0)
}

/**
 * Owns the filter sidebar and the applied-filter summary (count + pills), which stays visible
 * behind the sidebar while it's open so the current selection is never hidden.
 *
 * The sidebar's form fields hold a *draft* of the filters as the user edits them; that draft is
 * only promoted to "applied" (and only then does it drive the summary or a new search) via
 * Apply/Reset or a pill removal. Dismissing the sidebar any other way discards the draft.
 */
const SearchFilter = <K extends object>({
    filterFieldCollections,
    onFilterValueChange,
    onApplyFilters,
    methods,
    onSubmit,
    defaultFilterValues,
    sortOptions,
    sortFilterKey,
    sortValue,
    onSortChange,
}: SearchFilterProps<K>) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [appliedFilterValues, setAppliedFilterValues] = useState<FilterValues<K> | undefined>(defaultFilterValues)

    // Sorting is independent of filtering: the controls row must render whenever either is available.
    const hasFilterFields = !!filterFieldCollections?.length
    const hasSortOptions = !!sortOptions?.length

    // A sortFilterKey field has its own dedicated dropdown, so it shouldn't also show up as a
    // filter pill or count toward the "Filters (N)" badge.
    const filterValuesForSummary = useMemo(() => {
        if (!sortFilterKey || !appliedFilterValues) {
            return appliedFilterValues
        }
        const entries = Object.entries(appliedFilterValues).filter(([key]) => key !== sortFilterKey)
        return Object.fromEntries(entries) as FilterValues<K>
    }, [appliedFilterValues, sortFilterKey])

    const filterCount = useMemo(() => countActiveFilters(filterValuesForSummary), [filterValuesForSummary])

    /** Promotes `filters` to the applied state, optionally syncing the sidebar's draft to match, then searches. */
    const applyFilters = (filters: FilterValues<K> | undefined, syncDraft = false) => {
        const cleaned = stripEmptyFilters(filters) as FilterValues<K> | undefined

        if (syncDraft) {
            methods.setValue('filter' as never, cleaned as never, { shouldDirty: false })
        }

        setAppliedFilterValues(cleaned)
        onFilterValueChange?.(cleaned)

        if (onApplyFilters) {
            onApplyFilters(cleaned)
        } else {
            methods.handleSubmit(onSubmit)()
        }
    }

    // When sortFilterKey is set, the dropdown mirrors that field of the applied filters instead of
    // a value the parent tracks separately, so it and a same-named filter-sidebar field can't disagree.
    const resolvedSortValue = sortFilterKey
        ? ((appliedFilterValues as Record<string, unknown> | undefined)?.[sortFilterKey] as string | undefined)
        : sortValue
    const resolvedOnSortChange = sortFilterKey
        ? (value: string) => applyFilters({ ...appliedFilterValues, [sortFilterKey]: value } as FilterValues<K>, true)
        : onSortChange

    /** Apply/Reset: the draft already reflects what the user wants, so just promote it. */
    const onFilterButtonClick = () => {
        setIsSidebarOpen(false)
        applyFilters(methods.getValues().filter as FilterValues<K> | undefined)
    }

    /** Removing a pill changes the applied filters directly, so the draft must be synced to match. */
    const onRemoveFilter = (filters: FilterValues<K> | undefined) => {
        applyFilters(filters, true)
    }

    /** Dismissing the sidebar without Apply/Reset discards any unsaved edits to the draft. */
    const onSidebarClose = () => {
        setIsSidebarOpen(false)
        methods.setValue('filter' as never, appliedFilterValues as never, { shouldDirty: false })
    }

    return (
        <>
            <div className={clsx('sidebar-wrapper', isSidebarOpen ? 'open' : 'closed')}>
                {hasFilterFields && (
                    <Sidebar onClose={onSidebarClose}>
                        <FilterSection
                            filterCollections={filterFieldCollections}
                            onApplyFilter={onFilterButtonClick}
                            onResetFilter={onFilterButtonClick}
                            defaultFilterValues={defaultFilterValues}
                        />
                    </Sidebar>
                )}
            </div>
            {(hasFilterFields || hasSortOptions) && (
                <FilterControls
                    filterFieldCollections={filterFieldCollections}
                    setIsSidebarOpen={setIsSidebarOpen}
                    sortOptions={sortOptions}
                    sortValue={resolvedSortValue}
                    onSortChange={resolvedOnSortChange}
                    sortFilterKey={sortFilterKey}
                    appliedFilterValues={appliedFilterValues}
                    onRemoveFilter={onRemoveFilter}
                    filterCount={filterCount}
                />
            )}
        </>
    )
}

export default SearchFilter
